// Data status strip: one chip per data source saying how its data arrives (live, or every N
// minutes), when it last synced successfully (reporting time zone) and whether it is healthy.
import { formatClockTime } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import type { RegistryEntry, WidgetComponentProps } from '../types';
import { summarise, type SourceStatus } from './data-status-model.ts';

const HEALTH_TEXT: Record<SourceStatus['health'], string> = { ok: 'Up to date', syncing: 'Syncing', waiting: 'First sync pending', problem: 'Needs attention' };

function DataStatus({ data, props }: WidgetComponentProps) {
  const sources = summarise(data?.connectors ?? [], props.connectors);
  if (sources.length === 0) {
    return (
      <div className="state">
        <strong>No data sources connected</strong>
        Connect Shopify and your ad and analytics accounts on Settings → Connections.
      </div>
    );
  }
  return (
    <ul className="data-status" aria-label="Data sources">
      {sources.map((s) => {
        const at = s.lastSuccessAt ? formatClockTime(s.lastSuccessAt) : 'never';
        const detail = [
          `${s.label}: ${s.cadence === 'realtime' ? 'real-time (webhooks)' : `refreshed ${s.every === 'Hourly' ? 'hourly' : `every ${s.every}`}`}`,
          `Last successful sync: ${at}${s.stores > 1 ? ` (oldest of ${s.stores} stores)` : ''}`,
          HEALTH_TEXT[s.health],
          ...s.problems,
        ].join('\n');
        return (
          <li key={s.id} className={`ds-item ${s.health}`} title={detail}>
            <span className={`ds-dot ${s.health}`} aria-hidden />
            <span className="ds-name">{s.label}</span>
            <span className={`ds-cadence ${s.cadence}`}>
              {s.cadence === 'realtime' ? <span className="live-dot" aria-hidden /> : <Icon name="clock" size={11} />}
              {s.every}
            </span>
            <span className="ds-at">
              <span className="sr-only">{HEALTH_TEXT[s.health]}. Last successful sync </span>
              {s.health === 'syncing' ? (
                <>
                  <Icon name="refresh" size={11} className="spin" /> Syncing · {at}
                </>
              ) : s.health === 'problem' ? (
                <span className="error-text">Needs attention</span>
              ) : (
                at
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export const dataStatus: RegistryEntry = {
  component: DataStatus,
  toTable: (d, w) => ({
    columns: [
      { key: 'label', label: 'Source' },
      { key: 'every', label: 'Updates' },
      { key: 'lastSuccessAt', label: 'Last successful sync', format: 'datetime' },
      { key: 'health', label: 'Status' },
    ],
    rows: summarise(d?.connectors ?? [], (w.props as any)?.connectors).map((s) => ({ ...s, health: HEALTH_TEXT[s.health] })),
  }),
};
